import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  HeartHandshake, Eye, Award, Sparkles, ArrowRight, ArrowUpRight,
  PawPrint, Quote, Building2, Stethoscope, Microscope, Heart,
} from "lucide-react";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import SeoHead from "../components/SeoHead";
import { CLINICS } from "../data/clinics";
import { CLINICAS_DETALHE } from "../data/clinicasDetalhe";

const VALORES = [
  {
    icon: HeartHandshake,
    titulo: "Proximidade",
    texto: "Conhecemos cada animal pelo nome. Acompanhamos cada tutor de perto, do primeiro contacto à recuperação.",
  },
  {
    icon: Eye,
    titulo: "Transparência",
    texto: "Explicamos todos os procedimentos, custos e opções. Decisões informadas são melhores decisões.",
  },
  {
    icon: Award,
    titulo: "Competência",
    texto: "Formação contínua, equipamento moderno e protocolos atualizados. A medicina veterinária evolui, e nós com ela.",
  },
  {
    icon: Sparkles,
    titulo: "Bem-estar animal",
    texto: "Tudo o que fazemos tem um único objetivo: o bem-estar e a qualidade de vida do seu companheiro.",
  },
];

const INSTALACOES = [
  { url: "/clinic-photos/entrada-1.jpg", legenda: "Entrada e zona de receção" },
  { url: "/clinic-photos/rececao.jpg", legenda: "Balcão de atendimento" },
  { url: "/clinic-photos/rececao-consultorio.jpg", legenda: "Acesso ao consultório" },
  { url: "/clinic-photos/laboratorio.jpg", legenda: "Laboratório e diagnóstico" },
  { url: "/clinic-photos/loja-suplementos.jpg", legenda: "Suplementação e nutrição especializada" },
  { url: "/clinic-photos/loja-2.jpg", legenda: "Acessórios, transporte e cuidados diários" },
];

const PARCEIROS = [
  {
    nome: "ASARB",
    descricao: "Associação Social Animal da Ribeira Brava — proteção, esterilização e adoção responsável.",
  },
  {
    nome: "VAMOS LÁ MADEIRA",
    descricao: "Movimento social pela causa animal e bem-estar comunitário na Madeira.",
  },
  {
    nome: "ANIMA",
    descricao: "Resgate, recuperação e procura de família para animais em risco.",
  },
  {
    nome: "VETCARE",
    descricao: "Rede de partilha clínica entre profissionais — formação contínua e segunda opinião.",
  },
];

export default function SobrePage() {
  return (
    <div className="min-h-screen bg-[#FAF7F2] text-[#1A1A1A]" data-testid="sobre-page">
      <SeoHead
        title="Sobre nós | Vet Clinic — Madeira"
        description="Desde 2006 a cuidar dos animais da Madeira. Duas clínicas, Ribeira Brava e Calheta, sob direção clínica da Dr.ª Florinda Santos."
      />
      <Header />
      <main>
        {/* Hero */}
        <section className="relative overflow-hidden" data-testid="sobre-hero">
          <div
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(900px 420px at 85% -10%, rgba(30,95,110,0.12), transparent 60%), linear-gradient(180deg, #FAF7F2 0%, #EFE9DF 100%)",
            }}
          />
          <div className="absolute inset-0 grain opacity-50 pointer-events-none" />
          <div className="container-app relative pt-16 pb-16 md:pt-24 md:pb-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#88A096] mb-3">
                Quem somos
              </span>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-[64px] font-extrabold leading-[1.05] tracking-tight">
                Veterinários por profissão, <span className="text-[#1E5F6E]">cuidadores</span> por vocação.
              </h1>
              <p className="mt-5 text-lg md:text-xl text-[#5A5A5A] max-w-2xl leading-relaxed">
                Desde 2006 a cuidar dos animais da Madeira. Duas clínicas, uma só forma de cuidar — com proximidade, competência e o respeito que cada animal merece.
              </p>
            </div>
            <div className="lg:col-span-5">
              <div className="relative aspect-[4/3] rounded-3xl overflow-hidden border border-[#1E5F6E]/10 shadow-[0_30px_60px_-30px_rgba(15,58,68,0.35)]">
                <img
                  src="/clinic-photos/brand-logo.jpg"
                  alt="Logótipo Vet Clinic em parede de madeira"
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* História */}
        <section className="container-app py-16 md:py-24" data-testid="sobre-historia">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-6 lg:order-2">
              <div className="relative aspect-[4/5] max-w-md mx-auto lg:mx-0 rounded-3xl overflow-hidden border border-[#1E5F6E]/10 shadow-soft">
                <img
                  src="/clinic-photos/exterior-edificio.jpg"
                  alt="Fachada da clínica veterinária na Madeira"
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="lg:col-span-6 lg:order-1">
              <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#88A096] mb-3">
                A nossa história
              </span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#1A1A1A] tracking-tight">
                Cuidamos da Madeira <span className="text-[#1E5F6E]">desde 2006</span>.
              </h2>
              <div className="mt-5 text-[#5A5A5A] leading-relaxed space-y-4 text-[15.5px]">
                <p>
                  A Vet Clinic nasceu em 2006, em Ribeira Brava, com um propósito simples:
                  oferecer aos animais da Madeira uma medicina veterinária próxima, rigorosa
                  e profundamente humana. Ao longo dos anos crescemos junto das famílias que
                  nos confiam os seus companheiros — e crescemos também enquanto equipa.
                </p>
                <p>
                  Sob direção clínica da <strong>Dr.ª Florinda Santos</strong>, alargámos a
                  oferta para a Calheta com o <em>Consultório Veterinário da Estrela da Calheta</em>,
                  trazendo o mesmo padrão de cuidado à costa Oeste da ilha. Duas portas, uma só
                  equipa, a mesma forma de cuidar.
                </p>
                <p>
                  Hoje somos referência regional em medicina preventiva, cirurgia e
                  diagnóstico — sem nunca perder aquilo que sempre nos definiu: o tempo que
                  damos a cada animal e a cada pessoa que entra à nossa porta.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Missão + Visão */}
        <section className="bg-white border-y border-[#1E5F6E]/8 py-16 md:py-24" data-testid="sobre-missao-visao">
          <div className="container-app grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5 }}
              data-testid="bloco-missao"
              className="rounded-3xl bg-[#FAF7F2] border border-[#1E5F6E]/10 p-8 md:p-10"
            >
              <span className="grid h-12 w-12 place-items-center rounded-2xl bg-[#1E5F6E] text-white mb-5">
                <Heart className="h-5 w-5" strokeWidth={2.2} />
              </span>
              <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#88A096] mb-2">
                Missão
              </span>
              <h2 className="font-display text-2xl md:text-3xl font-bold text-[#1A1A1A] tracking-tight">
                Cuidar com <span className="text-[#1E5F6E]">competência</span> e carinho.
              </h2>
              <p className="mt-5 text-[15.5px] text-[#5A5A5A] leading-relaxed">
                Promover a saúde e o bem-estar dos animais de companhia da Madeira através
                de uma medicina veterinária baseada em evidência, próxima das famílias e
                respeitadora de cada animal — do primeiro check-up ao fim de vida digno.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: 0.08 }}
              data-testid="bloco-visao"
              className="rounded-3xl bg-[#FAF7F2] border border-[#1E5F6E]/10 p-8 md:p-10"
            >
              <span className="grid h-12 w-12 place-items-center rounded-2xl bg-[#E8A87C] text-[#1A1A1A] mb-5">
                <Eye className="h-5 w-5" strokeWidth={2.2} />
              </span>
              <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#88A096] mb-2">
                Visão
              </span>
              <h2 className="font-display text-2xl md:text-3xl font-bold text-[#1A1A1A] tracking-tight">
                A referência veterinária da <span className="text-[#1E5F6E]">Madeira</span>.
              </h2>
              <p className="mt-5 text-[15.5px] text-[#5A5A5A] leading-relaxed">
                Ser a escolha natural das famílias da Madeira para o cuidado dos seus animais
                — combinando excelência clínica, tecnologia atualizada e uma relação humana
                que faz toda a diferença em cada visita.
              </p>
            </motion.div>
          </div>

          <motion.figure
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6 }}
            className="container-app mt-12 relative max-w-3xl"
          >
            <Quote className="absolute -top-4 left-7 h-8 w-8 text-[#E8A87C] bg-white rounded-xl p-1.5 border border-[#1E5F6E]/10" />
            <blockquote className="rounded-3xl bg-[#FAF7F2] border border-[#1E5F6E]/10 p-7 md:p-9 font-display text-lg md:text-xl font-semibold text-[#1A1A1A] leading-snug">
              &ldquo;Cada animal tem nome, história e família. É assim que olhamos para todos os que
              entram à nossa porta há quase duas décadas.&rdquo;
              <footer className="mt-4 text-sm font-normal text-[#5A5A5A] not-italic">
                — Dr.ª Florinda Santos, Diretora Clínica
              </footer>
            </blockquote>
          </motion.figure>
        </section>

        {/* Valores */}
        <section className="container-app py-16 md:py-24" data-testid="sobre-valores">
          <div className="max-w-2xl mb-12">
            <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#88A096] mb-3">
              Os nossos valores
            </span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1A1A1A] tracking-tight">
              O que nos <span className="text-[#1E5F6E]">guia</span>.
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 lg:gap-6">
            {VALORES.map((v, i) => {
              const Icon = v.icon;
              return (
                <motion.article
                  key={v.titulo}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.5, delay: (i % 2) * 0.08 }}
                  data-testid={`valor-${v.titulo.toLowerCase()}`}
                  className="group rounded-3xl bg-white border border-[#1E5F6E]/10 p-7 md:p-8 hover:-translate-y-1 hover:shadow-soft transition-all"
                >
                  <div className="flex items-start gap-4">
                    <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-[#1E5F6E] to-[#0F3A44] text-white shadow-[0_8px_20px_-8px_rgba(15,58,68,0.5)] group-hover:scale-105 transition-transform">
                      <Icon className="h-5 w-5" strokeWidth={2.1} />
                    </span>
                    <div>
                      <h3 className="font-display text-xl md:text-2xl font-bold text-[#1A1A1A]">
                        {v.titulo}
                      </h3>
                      <p className="mt-2 text-[15px] text-[#5A5A5A] leading-relaxed">
                        {v.texto}
                      </p>
                    </div>
                  </div>
                </motion.article>
              );
            })}
          </div>
        </section>

        {/* Instalações */}
        <section className="bg-white border-y border-[#1E5F6E]/8 py-16 md:py-24" data-testid="sobre-instalacoes">
          <div className="container-app">
            <div className="max-w-2xl mb-10">
              <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#88A096] mb-3">
                As nossas instalações
              </span>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1A1A1A] tracking-tight">
                Um espaço pensado para os <span className="text-[#1E5F6E]">animais</span>.
              </h2>
              <p className="mt-4 text-[#5A5A5A] leading-relaxed max-w-xl">
                Receção acolhedora, consultório dedicado, laboratório próprio para diagnóstico
                e uma loja com alimentação e suplementação especializada — tudo no mesmo
                espaço, para que nada falte ao seu companheiro.
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-5">
              {INSTALACOES.map((img, i) => (
                <motion.figure
                  key={img.url}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.45, delay: (i % 3) * 0.06 }}
                  data-testid={`instalacao-${i}`}
                  className="group relative aspect-[4/3] overflow-hidden rounded-2xl border border-[#1E5F6E]/10 shadow-soft"
                >
                  <img
                    src={img.url}
                    alt={img.legenda}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <figcaption className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/70 via-black/20 to-transparent text-white text-xs font-semibold">
                    {img.legenda}
                  </figcaption>
                </motion.figure>
              ))}
            </div>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
              {[
                { icon: Building2, t: "Receção e zona comercial", d: "Acesso fácil, atendimento dedicado e produtos especializados ao seu dispor." },
                { icon: Stethoscope, t: "Consultório", d: "Espaço calmo e equipado para consulta, vacinação e pequenos procedimentos." },
                { icon: Microscope, t: "Laboratório próprio", d: "Análises e citologias no momento — diagnóstico mais rápido para o seu animal." },
              ].map((f, i) => {
                const Icon = f.icon;
                return (
                  <div key={i} className="rounded-2xl bg-[#FAF7F2] border border-[#1E5F6E]/10 p-5">
                    <span className="grid h-10 w-10 place-items-center rounded-xl bg-[#1E5F6E] text-white mb-3">
                      <Icon className="h-4.5 w-4.5" strokeWidth={2.2} />
                    </span>
                    <div className="font-display font-bold text-[#1A1A1A]">{f.t}</div>
                    <p className="mt-1 text-sm text-[#5A5A5A] leading-relaxed">{f.d}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Compromisso Social - Parceiros */}
        <section className="container-app py-16 md:py-24" data-testid="sobre-compromisso">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#88A096] mb-3">
                Compromisso social
              </span>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1A1A1A] tracking-tight">
                Apoiamos quem <span className="text-[#1E5F6E]">apoia</span> os animais.
              </h2>
              <p className="mt-5 text-[#5A5A5A] leading-relaxed">
                Cuidar dos animais não termina à porta da clínica. Colaboramos com
                associações e movimentos que dedicam o seu tempo aos animais
                abandonados, à esterilização responsável e à adoção consciente na Madeira.
              </p>
              <div className="mt-7 rounded-2xl bg-[#88A096]/10 border border-[#88A096]/30 p-5 text-sm text-[#1A1A1A]">
                <strong>Quer fazer a diferença?</strong> Apoie qualquer um dos nossos parceiros
                ou fale connosco sobre como contribuir.
              </div>
            </div>
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
              {PARCEIROS.map((p, i) => (
                <motion.article
                  key={p.nome}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.45, delay: (i % 2) * 0.06 }}
                  data-testid={`parceiro-${p.nome.toLowerCase().replace(/\s+/g, "-")}`}
                  className="rounded-2xl bg-white border border-[#1E5F6E]/10 p-5 md:p-6 hover:-translate-y-0.5 hover:shadow-soft transition-all"
                >
                  <div className="flex items-start gap-3">
                    <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-[#E8A87C]/20 text-[#1E5F6E] font-display font-extrabold text-sm">
                      {p.nome.slice(0, 2)}
                    </span>
                    <div>
                      <div className="font-display text-base md:text-lg font-bold text-[#1A1A1A] tracking-tight">
                        {p.nome}
                      </div>
                      <p className="mt-1.5 text-[13.5px] text-[#5A5A5A] leading-relaxed">
                        {p.descricao}
                      </p>
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          </div>
        </section>

        {/* Clínicas mini-cards */}
        <section className="bg-white border-y border-[#1E5F6E]/8 py-16 md:py-24" data-testid="sobre-clinicas">
          <div className="container-app">
            <div className="max-w-2xl mb-10">
              <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#88A096] mb-3">
                As nossas clínicas
              </span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#1A1A1A] tracking-tight">
                Visite-nos.
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {CLINICS.map((c) => {
                const det = CLINICAS_DETALHE[c.slug];
                return (
                  <Link
                    key={c.slug}
                    to={`/clinicas/${c.slug}`}
                    data-testid={`sobre-clinica-${c.slug}`}
                    className="group relative overflow-hidden rounded-3xl border border-[#1E5F6E]/10 bg-[#FAF7F2] hover:-translate-y-1 hover:shadow-glow transition-all"
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={det.heroImage}
                        alt={det.heroAlt}
                        className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-6">
                      <div className="text-xs font-bold uppercase tracking-widest text-[#88A096]">
                        {c.cidade}
                      </div>
                      <h3 className="mt-1 font-display text-xl md:text-2xl font-bold text-[#1A1A1A]">
                        {c.nome}
                      </h3>
                      <p className="mt-1.5 text-sm text-[#5A5A5A]">{c.morada}</p>
                      <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-[#1E5F6E] group-hover:gap-2 transition-all">
                        Visitar
                        <ArrowUpRight className="h-4 w-4" />
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="px-5 sm:px-8 lg:px-10 py-20" data-testid="sobre-cta">
          <div className="relative overflow-hidden rounded-[40px] bg-[#1E5F6E] text-white max-w-7xl mx-auto px-6 md:px-14 py-16 md:py-20 text-center">
            <div className="absolute inset-0 grain opacity-50 pointer-events-none" />
            <div className="absolute -left-12 top-1/2 -translate-y-1/2 h-60 w-60 rounded-full bg-[#88A096]/30" aria-hidden />
            <div className="absolute -right-10 -top-8 h-44 w-44 rounded-full bg-[#E8A87C]/20" aria-hidden />
            <div className="relative">
              <span className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur px-4 py-1.5 text-xs font-bold uppercase tracking-widest border border-white/15">
                <PawPrint className="h-3.5 w-3.5" />
                Ficamos à sua espera
              </span>
              <h2 className="mt-6 font-display text-3xl md:text-4xl lg:text-[48px] font-extrabold leading-tight tracking-tight">
                Pronto para nos conhecer <span className="text-[#E8A87C]">melhor?</span>
              </h2>
              <Link
                to="/marcar-consulta"
                data-testid="sobre-cta-btn"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#E8A87C] text-[#1A1A1A] px-7 py-4 text-base font-bold hover:bg-[#D99A6E] hover:-translate-y-0.5 transition-all"
              >
                Marcar consulta
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
